import { Switch, Route, useLocation } from "wouter";
import { Analytics } from "@vercel/analytics/react";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { useEffect, lazy, Suspense } from "react";
import { PageSpinner } from "@/components/ui/loading";
import { ThemeProvider } from "@/components/theme-provider";
import { CanonicalTag } from "@/components/seo/CanonicalTag";

import { PublicHeader } from "@/components/layout/PublicHeader";
import { DashboardHeader } from "@/components/layout/DashboardHeader";

import Landing from "@/pages/landing";
const Dashboard = lazy(() => import("@/pages/dashboard"));
const Create = lazy(() => import("@/pages/create"));
const PostDetail = lazy(() => import("@/pages/post-detail"));
const Posts = lazy(() => import("@/pages/posts"));
const Blog = lazy(() => import("@/pages/blog"));
const BlogPost = lazy(() => import("@/pages/blog-post"));
const FAQ = lazy(() => import("@/pages/faq"));
const About = lazy(() => import("@/pages/about"));
const HowItWorks = lazy(() => import("@/pages/how-it-works"));
const Pricing = lazy(() => import("@/pages/pricing"));
const Contact = lazy(() => import("@/pages/contact"));
const CompareReddit = lazy(() => import("@/pages/compare-reddit"));
const CompareFiverr = lazy(() => import("@/pages/compare/fiverr"));
const CompareUpwork = lazy(() => import("@/pages/compare/upwork"));
const ComparePhotoshop = lazy(() => import("@/pages/compare/photoshop"));
const CompareRemoveBg = lazy(() => import("@/pages/compare/removebg"));
const CompareCanva = lazy(() => import("@/pages/compare/canva"));
const ComparePixelPhant = lazy(() => import("@/pages/compare/pixelphant"));
const ComparePhotoRoom = lazy(() => import("@/pages/compare/photoroom"));
const CompareTopazPhotoAI = lazy(() => import("@/pages/compare/topaz-photo-ai"));
const CompareLuminarNeo = lazy(() => import("@/pages/compare/luminar-neo"));
const ComparePicsArt = lazy(() => import("@/pages/compare/picsart"));
const CompareSnapseed = lazy(() => import("@/pages/compare/snapseed"));
const CompareVSCO = lazy(() => import("@/pages/compare/vsco"));
const CompareClippingMagic = lazy(() => import("@/pages/compare/clipping-magic"));
const CompareRemovalAI = lazy(() => import("@/pages/compare/removal-ai"));
const AuthCallback = lazy(() => import("@/pages/auth-callback"));
const PaymentSuccess = lazy(() => import("@/pages/payment-success"));
const NotFound = lazy(() => import("@/pages/not-found"));
const Services = lazy(() => import("@/pages/services"));
const PersonRemoval = lazy(() => import("@/pages/services/person-removal"));
const ImageEnhancement = lazy(() => import("@/pages/services/image-enhancement"));
const BackgroundRemoval = lazy(() => import("@/pages/services/background-removal"));
const ColorCorrection = lazy(() => import("@/pages/services/color-correction"));
const AnimalEditing = lazy(() => import("@/pages/services/animal-editing"));
const TextLogoAddition = lazy(() => import("@/pages/services/text-logo-addition"));
const ObjectRemoval = lazy(() => import("@/pages/services/object-removal"));
const StyleFilter = lazy(() => import("@/pages/services/style-filter"));
const ImageUpscaling = lazy(() => import("@/pages/services/image-upscaling"));
const PhotoRestoration = lazy(() => import("@/pages/services/photo-restoration"));
const PortraitRetouching = lazy(() => import("@/pages/services/portrait-retouching"));
const ColorizePhotos = lazy(() => import("@/pages/services/colorize-photos"));
const PerspectiveCorrection = lazy(() => import("@/pages/services/perspective-correction"));
const EyeSmileEnhancement = lazy(() => import("@/pages/services/eye-smile-enhancement"));
const ProductPhotos = lazy(() => import("@/pages/services/product-photos"));
const BlemishRemoval = lazy(() => import("@/pages/services/blemish-removal"));
const NoiseReduction = lazy(() => import("@/pages/services/noise-reduction"));
const ArtisticEffects = lazy(() => import("@/pages/services/artistic-effects"));
const MotionBlurFix = lazy(() => import("@/pages/services/motion-blur-fix"));
const CloneDuplicate = lazy(() => import("@/pages/services/clone-duplicate"));
const BackgroundBlur = lazy(() => import("@/pages/services/background-blur"));
const SkyReplacement = lazy(() => import("@/pages/services/sky-replacement"));
const WatermarkRemoval = lazy(() => import("@/pages/services/watermark-removal"));
const FaceSwap = lazy(() => import("@/pages/services/face-swap"));
const HairEditing = lazy(() => import("@/pages/services/hair-editing"));
const BodyEditing = lazy(() => import("@/pages/services/body-editing"));
const ClothingRecolor = lazy(() => import("@/pages/services/clothing-recolor"));
const ImageMerge = lazy(() => import("@/pages/services/image-merge"));
const VintageEffects = lazy(() => import("@/pages/services/vintage-effects"));
const SpecializedEditing = lazy(() => import("@/pages/services/specialized-editing"));

// New Service Pages (Nov 2025)
const ChristmasPhotoEditing = lazy(() => import("@/pages/services/christmas-photo-editing"));
const AddDeceasedLovedOne = lazy(() => import("@/pages/services/add-deceased-loved-one"));
const RemoveExFromPhoto = lazy(() => import("@/pages/services/remove-ex-from-photo"));
const FixClosedEyes = lazy(() => import("@/pages/services/fix-closed-eyes"));
const LinkedinHeadshotMaker = lazy(() => import("@/pages/services/linkedin-headshot-maker"));
const RealEstatePhotoEditing = lazy(() => import("@/pages/services/real-estate-photo-editing"));
const RemoveGlareGlasses = lazy(() => import("@/pages/services/remove-glare-glasses"));
const DatingProfileEnhancer = lazy(() => import("@/pages/services/dating-profile-enhancer"));
const PassportVisaPhoto = lazy(() => import("@/pages/services/passport-visa-photo"));
const TattooMockup = lazy(() => import("@/pages/services/tattoo-mockup"));
const FixAiPhotoErrors = lazy(() => import("@/pages/services/fix-ai-photo-errors"));
const ScreenshotCleaner = lazy(() => import("@/pages/services/screenshot-cleaner"));
const ChangeTextInImage = lazy(() => import("@/pages/services/change-text-in-image"));
const Specialities = lazy(() => import("@/pages/specialities"));
const AllDemos = lazy(() => import("@/pages/all-demos"));

const AmbassadorLogin = lazy(() => import("@/pages/ambassador/login"));
const AmbassadorDashboard = lazy(() => import("@/pages/ambassador/dashboard"));
const PostDetailPage = lazy(() => import("@/pages/ambassador/post-detail"));
const AmbassadorPostsPage = lazy(() => import("@/pages/ambassador/posts"));
const DownloadPage = lazy(() => import("@/pages/download/[id]"));
const AdminDashboard = lazy(() => import("@/pages/admin/dashboard"));
const AdminUsers = lazy(() => import("@/pages/admin/users"));
const AdminUserDetail = lazy(() => import("@/pages/admin/user-detail"));
const AdminPosts = lazy(() => import("@/pages/admin/posts"));
const AdminPostDetail = lazy(() => import("@/pages/admin/post-detail"));
const AdminGenerations = lazy(() => import("@/pages/admin/generations"));
const AdminSettings = lazy(() => import("@/pages/admin/ambassador-settings"));
const AdminPaymentAnalytics = lazy(() => import("@/pages/admin/payment-analytics"));

const MemorialLanding = lazy(() => import("@/pages/MemorialLanding"));
const VideoDashboard = lazy(() => import("@/pages/VideoDashboard"));

// Component to scroll to top on route change
function ScrollToTop() {
  const [location] = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, [location]);

  return null;
}

function Router() {
  return (
    <Switch>
      {/* Public Routes - Landing is not lazy loaded to prevent hydration mismatch */}
      <Route path="/" component={Landing} />

      {/* All other routes are lazy loaded and wrapped in Suspense */}
      <Route>
        <Suspense fallback={<PageSpinner />}>
          <Switch>
            <Route path="/blog" component={Blog} />
            <Route path="/blog/:slug" component={BlogPost} />
            <Route path="/faq" component={FAQ} />
            <Route path="/about" component={About} />
            <Route path="/how-it-works" component={HowItWorks} />
            <Route path="/pricing" component={Pricing} />
            <Route path="/auth/callback" component={AuthCallback} />
            <Route path="/contact" component={Contact} />

            {/* Ambassador Routes */}
            <Route path="/ambassador/login" component={AmbassadorLogin} />
            <Route path="/download/:id" component={DownloadPage} />

            {/* Admin Routes - Should be protected by role in real app */}
            <Route path="/admin">
              <ProtectedRoute>
                <AdminDashboard />
              </ProtectedRoute>
            </Route>
            <Route path="/admin/users">
              <ProtectedRoute>
                <AdminUsers />
              </ProtectedRoute>
            </Route>
            <Route path="/admin/users/:id">
              <ProtectedRoute>
                <AdminUserDetail />
              </ProtectedRoute>
            </Route>
            <Route path="/admin/posts">
              <ProtectedRoute>
                <AdminPosts />
              </ProtectedRoute>
            </Route>
            <Route path="/admin/posts/:id">
              <ProtectedRoute>
                <AdminPostDetail />
              </ProtectedRoute>
            </Route>
            <Route path="/admin/generations">
              <ProtectedRoute>
                <AdminGenerations />
              </ProtectedRoute>
            </Route>
            <Route path="/admin/ambassadors">
              <ProtectedRoute>
                <AdminSettings />
              </ProtectedRoute>
            </Route>
            <Route path="/admin/payments">
              <ProtectedRoute>
                <AdminPaymentAnalytics />
              </ProtectedRoute>
            </Route>

            {/* Comparison Pages */}
            <Route path="/compare/reddit" component={CompareReddit} />
            <Route path="/compare/fiverr" component={CompareFiverr} />
            <Route path="/compare/upwork" component={CompareUpwork} />
            <Route path="/compare/photoshop" component={ComparePhotoshop} />
            <Route path="/compare/removebg" component={CompareRemoveBg} />
            <Route path="/compare/canva" component={CompareCanva} />
            <Route path="/compare/pixelphant" component={ComparePixelPhant} />
            <Route path="/compare/photoroom" component={ComparePhotoRoom} />
            <Route path="/compare/topaz-photo-ai" component={CompareTopazPhotoAI} />
            <Route path="/compare/luminar-neo" component={CompareLuminarNeo} />
            <Route path="/compare/picsart" component={ComparePicsArt} />
            <Route path="/compare/snapseed" component={CompareSnapseed} />
            <Route path="/compare/vsco" component={CompareVSCO} />
            <Route path="/compare/clipping-magic" component={CompareClippingMagic} />
            <Route path="/compare/removal-ai" component={CompareRemovalAI} />

            {/* Service Landing Pages */}
            <Route path="/services" component={Services} />
            <Route path="/specialities" component={Specialities} />
            <Route path="/services/person-removal" component={PersonRemoval} />
            <Route path="/services/image-enhancement" component={ImageEnhancement} />
            <Route path="/services/background-removal" component={BackgroundRemoval} />
            <Route path="/services/color-correction" component={ColorCorrection} />
            <Route path="/services/animal-editing" component={AnimalEditing} />
            <Route path="/services/text-logo-addition" component={TextLogoAddition} />
            <Route path="/services/object-removal" component={ObjectRemoval} />
            <Route path="/services/style-filter" component={StyleFilter} />
            <Route path="/services/image-upscaling" component={ImageUpscaling} />
            <Route path="/services/photo-restoration" component={PhotoRestoration} />
            <Route path="/services/portrait-retouching" component={PortraitRetouching} />
            <Route path="/services/colorize-photos" component={ColorizePhotos} />
            <Route path="/services/perspective-correction" component={PerspectiveCorrection} />
            <Route path="/services/eye-smile-enhancement" component={EyeSmileEnhancement} />
            <Route path="/services/product-photos" component={ProductPhotos} />
            <Route path="/services/blemish-removal" component={BlemishRemoval} />
            <Route path="/services/noise-reduction" component={NoiseReduction} />
            <Route path="/services/artistic-effects" component={ArtisticEffects} />
            <Route path="/services/motion-blur-fix" component={MotionBlurFix} />
            <Route path="/services/clone-duplicate" component={CloneDuplicate} />
            <Route path="/services/background-blur" component={BackgroundBlur} />
            <Route path="/services/sky-replacement" component={SkyReplacement} />
            <Route path="/services/watermark-removal" component={WatermarkRemoval} />
            <Route path="/services/face-swap" component={FaceSwap} />
            <Route path="/services/hair-editing" component={HairEditing} />
            <Route path="/services/body-editing" component={BodyEditing} />
            <Route path="/services/clothing-recolor" component={ClothingRecolor} />
            <Route path="/services/image-merge" component={ImageMerge} />
            <Route path="/services/vintage-effects" component={VintageEffects} />
            <Route path="/services/specialized-editing" component={SpecializedEditing} />

            {/* New Service Pages (Nov 2025) */}
            <Route path="/services/christmas-photo-editing" component={ChristmasPhotoEditing} />
            <Route path="/services/add-deceased-loved-one" component={AddDeceasedLovedOne} />
            <Route path="/services/remove-ex-from-photo" component={RemoveExFromPhoto} />
            <Route path="/services/fix-closed-eyes" component={FixClosedEyes} />
            <Route path="/services/linkedin-headshot-maker" component={LinkedinHeadshotMaker} />
            <Route path="/services/real-estate-photo-editing" component={RealEstatePhotoEditing} />
            <Route path="/services/remove-glare-glasses" component={RemoveGlareGlasses} />
            <Route path="/services/dating-profile-enhancer" component={DatingProfileEnhancer} />
            <Route path="/services/passport-visa-photo" component={PassportVisaPhoto} />
            <Route path="/services/tattoo-mockup" component={TattooMockup} />
            <Route path="/services/fix-ai-photo-errors" component={FixAiPhotoErrors} />
            <Route path="/services/screenshot-cleaner" component={ScreenshotCleaner} />
            <Route path="/services/change-text-in-image" component={ChangeTextInImage} />

            {/* Video Creator Routes */}
            <Route path="/memorial" component={MemorialLanding} />
            <Route path="/video/create">
              <ProtectedRoute>
                <VideoDashboard />
              </ProtectedRoute>
            </Route>

            {/* Demos Gallery */}
            <Route path="/demos" component={AllDemos} />

            {/* Protected Routes - Require Authentication */}
            <Route path="/dashboard">
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            </Route>
            <Route path="/ambassador/dashboard">
              <ProtectedRoute>
                <AmbassadorDashboard />
              </ProtectedRoute>
            </Route>
            <Route path="/ambassador/posts/:id">
              <ProtectedRoute>
                <PostDetailPage />
              </ProtectedRoute>
            </Route>
            <Route path="/ambassador/posts">
              <ProtectedRoute>
                <AmbassadorPostsPage />
              </ProtectedRoute>
            </Route>
            <Route path="/create">
              <ProtectedRoute>
                <Create />
              </ProtectedRoute>
            </Route>
            <Route path="/posts">
              <ProtectedRoute>
                <Posts />
              </ProtectedRoute>
            </Route>
            <Route path="/post/:id">
              <ProtectedRoute>
                <PostDetail />
              </ProtectedRoute>
            </Route>
            <Route path="/payment/success">
              <ProtectedRoute>
                <PaymentSuccess />
              </ProtectedRoute>
            </Route>

            {/* 404 */}
            <Route component={NotFound} />
          </Switch>
        </Suspense>
      </Route>
    </Switch>
  );
}

// Determine which header to show based on route
function AppHeader() {
  const [location] = useLocation();

  // Dashboard routes use DashboardHeader
  const isDashboardRoute =
    location === "/dashboard" ||
    location === "/create" ||
    location === "/posts" ||
    location.startsWith("/post/") ||
    location === "/payment/success";

  return isDashboardRoute ? <DashboardHeader /> : <PublicHeader />;
}

function App() {

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider defaultTheme="dark" storageKey="vite-ui-theme">
        <TooltipProvider>
          <ScrollToTop />
          <CanonicalTag />
          <div className="min-h-screen flex flex-col">
            <AppHeader />
            <main className="flex-1">
              <Router />
            </main>
          </div>
          <Toaster />
          <Analytics />
        </TooltipProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
